import type { Locale } from '../user-preferences/locales';

// CFG-UC-1, CFG-UC-2, ADR-013: реестр параметров конфигурации — общий язык сервера и
// интерфейса. Параметры объявляются деревом: путь в дереве — ключ параметра
// («media.maxUploadMb») и путь в объекте конфигурации, тип объекта выводится из дерева.
// У параметра есть описание на языках интерфейса: его показывают «Настройки платформы».

export enum ParamType {
  Integer = 'integer',
  Number = 'number',
  Boolean = 'boolean',
  String = 'string',
  Enum = 'enum',
  IntegerList = 'integerList',
}

/** Кому видно значение: интерфейсу (public) или только процессу сервера (private). */
export enum ParamVisibility {
  Public = 'public',
  Private = 'private',
}

/** Когда действует сохранённое значение: сразу или со следующего запуска (CFG-UC-2.CC-2). */
export enum ParamApply {
  Live = 'live',
  Restart = 'restart',
}

/** Откуда взято действующее значение (CFG-UC-2.CC-1). */
export enum ParamSource {
  Default = 'default',
  Env = 'env',
  Saved = 'saved',
}

export type ParamValue = number | boolean | string | readonly number[];

/** Описание параметра на каждом языке интерфейса. */
export type ParamText = Readonly<Record<Locale, string>>;

export const about = (ru: string, en: string): ParamText => ({ ru, en });

export interface ParamDef<T extends ParamValue = ParamValue> {
  readonly type: ParamType;
  readonly default: T;
  /** Целое и число: границы значения; список: границы каждого элемента. */
  readonly min?: number;
  readonly max?: number;
  readonly values?: readonly string[];
  readonly minItems?: number;
  readonly maxItems?: number;
  readonly maxLength?: number;
  readonly visibility: ParamVisibility;
  readonly apply: ParamApply;
  /** CFG-UC-2.CC-3: false — меняется только окружением сервера. */
  readonly editable: boolean;
  /** Значение не показывается никому, даже администратору (CFG-RULE-1). */
  readonly secret: boolean;
  /** PREF-UC-3, PREF-UC-3.CC-1: пользователь может задать себе своё значение. Только у публичных live-параметров интерфейса. */
  readonly personal: boolean;
  /** CFG-UC-2.CC-6: основной параметр — его меняют при обычной эксплуатации; остальные второстепенные. */
  readonly primary: boolean;
  /** Переменная окружения GR_*, если параметр ею задаётся. */
  readonly env: string | null;
  readonly about: ParamText;
}

export interface ParamTree {
  readonly [name: string]: ParamDef | ParamTree;
}

/** Объект конфигурации по дереву параметров: в листьях — значения. */
export type ConfigOf<T> = { readonly [K in keyof T]: T[K] extends ParamDef<infer V> ? V : ConfigOf<T[K]> };

export interface ParamOptions {
  about: ParamText;
  visibility?: ParamVisibility;
  apply?: ParamApply;
  editable?: boolean;
  secret?: boolean;
  personal?: boolean;
  primary?: boolean;
  env?: string;
}

function define<T extends ParamValue>(type: ParamType, value: T, limits: Partial<ParamDef<T>>, options: ParamOptions): ParamDef<T> {
  return {
    type,
    default: value,
    ...limits,
    visibility: options.visibility ?? ParamVisibility.Private,
    apply: options.apply ?? ParamApply.Restart,
    editable: options.editable ?? true,
    secret: options.secret ?? false,
    personal: options.personal ?? false,
    primary: options.primary ?? false,
    env: options.env ?? null,
    about: options.about,
  };
}

/** Построители параметров. По умолчанию параметр приватный, действует с запуска, его можно менять. */
export const param = {
  int: (value: number, min: number, max: number, options: ParamOptions): ParamDef<number> => define(ParamType.Integer, value, { min, max }, options),
  number: (value: number, min: number, max: number, options: ParamOptions): ParamDef<number> => define(ParamType.Number, value, { min, max }, options),
  bool: (value: boolean, options: ParamOptions): ParamDef<boolean> => define(ParamType.Boolean, value, {}, options),
  string: (value: string, options: ParamOptions & { maxLength?: number }): ParamDef<string> =>
    define(ParamType.String, value, { maxLength: options.maxLength ?? 2000 }, options),
  oneOf: <T extends string>(values: readonly T[], value: T, options: ParamOptions): ParamDef<T> => define(ParamType.Enum, value, { values }, options),
  intList: (value: readonly number[], limits: { min: number; max: number; minItems: number; maxItems: number }, options: ParamOptions): ParamDef<readonly number[]> =>
    define(ParamType.IntegerList, value, limits, options),
};

/** Публичный параметр интерфейса: действует сразу. */
export const uiParam = (ru: string, en: string): ParamOptions => ({ visibility: ParamVisibility.Public, apply: ParamApply.Live, about: about(ru, en) });

/** Личный параметр интерфейса: пользователь задаёт себе своё значение в профиле (PREF-UC-3). */
export const personalUiParam = (ru: string, en: string): ParamOptions => ({ ...uiParam(ru, en), personal: true });

/** CFG-UC-2.CC-6: тот же параметр, но основной — показывается в «Основных» настройках платформы. */
export const primary = (options: ParamOptions): ParamOptions => ({ ...options, primary: true });

/**
 * CFG-UC-3, CFG-UC-3.CC-1: функция платформы — логический параметр features.*, действует сразу,
 * основной. Публичную функцию видит интерфейс (например, страница входа).
 */
export const feature = (env: string, ru: string, en: string, visibility: ParamVisibility = ParamVisibility.Private): ParamOptions => ({
  env,
  visibility,
  apply: ParamApply.Live,
  primary: true,
  about: about(ru, en),
});

export function isParamDef(node: ParamDef | ParamTree): node is ParamDef {
  return typeof (node as ParamDef).type === 'string' && 'default' in node && 'visibility' in node;
}

export interface FlatParam {
  key: string;
  def: ParamDef;
}

/** Все параметры дерева с ключами «раздел.область.параметр», в порядке объявления. */
export function flattenParams(tree: ParamTree, prefix = ''): FlatParam[] {
  return Object.entries(tree).flatMap(([name, node]) => {
    const key = prefix ? `${prefix}.${name}` : name;
    return isParamDef(node) ? [{ key, def: node }] : flattenParams(node, key);
  });
}

/** Значение по пути «a.b.c»; undefined — пути нет. */
export function getPath(source: unknown, key: string): unknown {
  return key.split('.').reduce<unknown>((node, name) => (node !== null && typeof node === 'object' ? (node as Record<string, unknown>)[name] : undefined), source);
}

/** Копия объекта с новым значением по пути; исходный объект не меняется. */
export function withPath<T>(source: T, key: string, value: unknown): T {
  const [head, ...rest] = key.split('.');
  const node = (source ?? {}) as Record<string, unknown>;
  return { ...node, [head!]: rest.length === 0 ? value : withPath(node[head!], rest.join('.'), value) } as T;
}

/** Объект конфигурации из значений по умолчанию. */
export function defaultsOf<T extends ParamTree>(tree: T): ConfigOf<T> {
  return flattenParams(tree).reduce<unknown>((config, { key, def }) => withPath(config, key, def.default), {}) as ConfigOf<T>;
}

/**
 * Проверка значения по типу и диапазону параметра (CFG-UC-2.CC-4). Возвращает значение в виде,
 * пригодном для конфигурации, или null — недопустимо.
 */
export function checkParamValue(def: ParamDef, value: unknown): ParamValue | null {
  const inRange = (n: number): boolean => (def.min === undefined || n >= def.min) && (def.max === undefined || n <= def.max);
  switch (def.type) {
    case ParamType.Integer:
      return typeof value === 'number' && Number.isInteger(value) && inRange(value) ? value : null;
    case ParamType.Number:
      return typeof value === 'number' && Number.isFinite(value) && inRange(value) ? value : null;
    case ParamType.Boolean:
      return typeof value === 'boolean' ? value : null;
    case ParamType.String:
      return typeof value === 'string' && value.length <= (def.maxLength ?? Number.MAX_SAFE_INTEGER) ? value : null;
    case ParamType.Enum:
      return typeof value === 'string' && (def.values ?? []).includes(value) ? value : null;
    case ParamType.IntegerList: {
      if (!Array.isArray(value)) return null;
      if (value.length < (def.minItems ?? 0) || value.length > (def.maxItems ?? Number.MAX_SAFE_INTEGER)) return null;
      return value.every((item) => typeof item === 'number' && Number.isInteger(item) && inRange(item)) ? [...(value as number[])] : null;
    }
  }
}

/** Значения равны (списки — поэлементно). */
export function sameParamValue(a: ParamValue | null | undefined, b: ParamValue | null | undefined): boolean {
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((item, index) => item === b[index]);
  return a === b;
}
